import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  EyeIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { questionBankAPI } from '../../services/api'
import toast from 'react-hot-toast'

interface Question {
  id: number
  title: string
  description: string
  difficulty_level: string
  question_data: any
  options?: any
  correct_answer?: any
  starter_code?: any[]
  language_restrictions?: string[]
}

const QuestionsPage: React.FC = () => {
  const { category, assessmentType } = useParams<{ category: string, assessmentType: string }>()
  const navigate = useNavigate()
  const [questions, setQuestions] = useState<Question[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  useEffect(() => {
    if (category && assessmentType) {
      loadQuestions()
    }
  }, [category, assessmentType])

  // Auto-refresh removed for simplicity

  const loadQuestions = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true)
        setError(null)
      } else {
        setRefreshing(true)
      }
      
      const questionType = assessmentType === 'coding' ? 'coding' : 
                          assessmentType === 'case-study' ? 'case_study' :
                          assessmentType === 'business-simulations' ? 'business_simulation' :
                          assessmentType === 'aptitude' ? 'aptitude' :
                          assessmentType === 'fundamentals' ? 'fundamentals' :
                          assessmentType === 'cloud_fundamentals' ? 'cloud_fundamentals' :
                          assessmentType === 'presales_fundamentals' ? 'presales_fundamentals' :
                          assessmentType === 'english_communication' ? 'english_communication' :
                          assessmentType === 'ai_fundamental' ? 'ai_fundamental' :
                          assessmentType === 'aiml_assignment' ? 'aiml_assignment' :
                          assessmentType === 'quantitative_ability' ? 'quantitative_ability' :
                          assessmentType === 'logical_reasoning' ? 'logical_reasoning' :
                          assessmentType === 'verbal_ability' ? 'verbal_ability' : 'multiple_choice'
      
      console.log('=== QUESTIONS PAGE DEBUG ===')
      console.log('Category:', category)
      console.log('Assessment Type:', assessmentType)
      console.log('Question Type:', questionType)
      console.log('API Parameters:', {
        category: category,
        question_type: questionType,
        is_verified: true,
        limit: 100
      })
      
      // First, test if the API endpoint is reachable using the actual endpoint with auth
      try {
        const token = localStorage.getItem('access_token')
        const testResponse = await fetch('/api/v1/question-pools/question-bank?limit=1', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        console.log('Backend connectivity test response:', testResponse.status)
        if (!testResponse.ok) {
          if (testResponse.status === 401) {
            throw new Error('Authentication failed. Please log in again.')
          }
          throw new Error(`Backend not reachable: ${testResponse.status}`)
        }
      } catch (testError: any) {
        console.error('Backend connectivity test failed:', testError)
        if (testError.message?.includes('Authentication failed')) {
          throw new Error('Authentication failed. Please log in again.')
        }
        throw new Error('Backend server is not running or not accessible. Please check if the backend is started.')
      }
      
      const response = await questionBankAPI.getQuestions({
        category: category,
        question_type: questionType,
        is_verified: true,
        limit: 100
      })
      
      console.log('API Response:', response)
      console.log('Response Data:', response.data)
      console.log('=== QUESTIONS PAGE DEBUG END ===')
      
      const newQuestions = response.data || []
      setQuestions(newQuestions)
      setLastRefresh(new Date())
      
      if (silent && newQuestions.length > questions.length) {
        toast.success(`Found ${newQuestions.length - questions.length} new questions!`)
      }
    } catch (error: any) {
      console.error('=== QUESTIONS PAGE ERROR ===')
      console.error('Error object:', error)
      console.error('Error message:', error.message)
      console.error('Error response:', error.response)
      console.error('Error status:', error.response?.status)
      console.error('Error data:', error.response?.data)
      console.error('=== QUESTIONS PAGE ERROR END ===')
      
      if (!silent) {
        let errorMessage = 'Failed to load questions'
        
        if (error.message?.includes('Authentication failed')) {
          errorMessage = 'Authentication failed. Please log in again.'
        } else if (error.response?.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.'
        } else if (error.message?.includes('Backend server is not running')) {
          errorMessage = 'Backend server is not running. Please start the backend server and try again.'
        } else if (error.response?.status === 404) {
          errorMessage = 'API endpoint not found. Please check if the backend is properly configured.'
        } else if (error.response?.status === 500) {
          errorMessage = 'Server error occurred. Please check the backend logs.'
        } else if (error.response?.data?.detail) {
          errorMessage = `Failed to load questions: ${error.response.data.detail}`
        } else if (error.message) {
          errorMessage = `Failed to load questions: ${error.message}`
        }
        
        setError(errorMessage)
      } else {
        toast.error('Failed to refresh questions')
      }
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [category, assessmentType, questions.length])

  const handleDeleteQuestion = async (questionId: number, questionTitle: string) => {
    // Show confirmation dialog with better UX
    const confirmed = window.confirm(
      `🗑️ Delete Question\n\nAre you sure you want to delete "${questionTitle || 'this question'}"?\n\n⚠️ This action cannot be undone and will permanently remove the question from the question bank.`
    )
    
    if (!confirmed) return

    setLoading(true)
    try {
      await questionBankAPI.deleteQuestion(questionId)
      toast.success(`✅ Question "${questionTitle || 'Question'}" deleted successfully!`)
      
      // Reload questions to update the list
      await loadQuestions(true) // Silent reload
    } catch (error: any) {
      console.error('Error deleting question:', error)
      toast.error(error.response?.data?.detail || 'Failed to delete question')
    } finally {
      setLoading(false)
    }
  }

  const getCategoryDisplayName = () => {
    if (!category) return ''
    const [collegeType, tier] = category.split('_')
    const tierDisplay = tier === 'tier1' ? 'Tier 1' : 'Tier 2'
    return `${collegeType.charAt(0).toUpperCase() + collegeType.slice(1)} ${tierDisplay}`
  }

  const getAssessmentTypeDisplayName = () => {
    const typeMap: { [key: string]: string } = {
      'coding': 'Coding Problems',
      'case-study': 'Case Studies',
      'business-simulations': 'Business Simulations',
      'aptitude': 'Aptitude & Reasoning',
      'fundamentals': 'Computer Fundamentals',
      'cloud_fundamentals': 'Cloud Fundamentals',
      'presales_fundamentals': 'Pre-Sales Fundamentals',
      'english_communication': 'English Communication',
      'ai_fundamental': 'AI Fundamental',
      'aiml_assignment': 'AI/ML Assignment',
      'quantitative_ability': 'Quantitative Ability',
      'logical_reasoning': 'Logical Reasoning',
      'verbal_ability': 'Verbal Ability'
    }
    return typeMap[assessmentType || ''] || assessmentType || ''
  }

  const getAssessmentTypeIcon = () => {
    // Removed emoji icons for cleaner look
    return ''
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading questions...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Questions</h3>
            <p className="text-red-700 text-sm mb-4">{error}</p>
            
            {error.includes('Backend server is not running') && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-yellow-800 mb-2">How to fix this:</h4>
                <div className="text-sm text-yellow-700 text-left space-y-2">
                  <p><strong>Option 1 - Quick Start:</strong></p>
                  <ol className="ml-4 space-y-1">
                    <li>1. Open a terminal/command prompt</li>
                    <li>2. Navigate to the project root directory</li>
                    <li>3. Run: <code className="bg-yellow-100 px-1 rounded">python start_backend.py</code></li>
                    <li>4. Wait for the server to start</li>
                    <li>5. Refresh this page</li>
                  </ol>
                  
                  <p className="mt-3"><strong>Option 2 - Manual Start:</strong></p>
                  <ol className="ml-4 space-y-1">
                    <li>1. Open a terminal/command prompt</li>
                    <li>2. Navigate to the backend directory</li>
                    <li>3. Run: <code className="bg-yellow-100 px-1 rounded">python -m uvicorn app.main:app --reload</code></li>
                    <li>4. Wait for the server to start</li>
                    <li>5. Refresh this page</li>
                  </ol>
                </div>
              </div>
            )}
            
            {error.includes('Authentication failed') && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-red-800 mb-2">Authentication Issue:</h4>
                <p className="text-sm text-red-700 mb-3">
                  Your session has expired or you're not properly logged in. Please log in again to access the question bank.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="btn btn-primary"
                >
                  Go to Login
                </button>
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={() => loadQuestions()}
              className="btn btn-outline flex items-center"
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              Try Again
            </button>
            <button
              onClick={() => navigate('/recruiter/dashboard')}
              className="btn btn-primary"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/recruiter/dashboard')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mr-4"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Dashboard
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {getAssessmentTypeDisplayName()}
              </h1>
              <p className="text-gray-600 mt-2">
                {getCategoryDisplayName()} • {questions.length} question{questions.length !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => loadQuestions()}
                disabled={refreshing || loading}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowPathIcon className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Questions List */}
        {questions.length > 0 ? (
          <div className="space-y-6">
            {questions.map((question, index) => (
              <div key={question.id || index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {question.title || `Question ${index + 1}`}
                      </h3>
                      <button
                        onClick={() => handleDeleteQuestion(question.id, question.title)}
                        disabled={loading}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
                        title="Delete question"
                      >
                        <TrashIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                      </button>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center">
                        <span className="font-medium">Difficulty:</span>
                        <span className={`ml-1 px-2 py-1 rounded-full text-xs font-medium ${
                          question.difficulty_level === 'easy' ? 'bg-green-100 text-green-800' :
                          question.difficulty_level === 'hard' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {question.difficulty_level ? question.difficulty_level.charAt(0).toUpperCase() + question.difficulty_level.slice(1) : 'Medium'}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Question Statement */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Problem Statement</label>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {question.question_data?.problem_statement || question.description || 'No problem statement available'}
                      </p>
                      
                      {/* Display question image if present */}
                      {question.question_data?.image && (
                        <div className="mt-4">
                          <img
                            src={question.question_data.image}
                            alt="Question diagram or figure"
                            className="max-w-full h-auto rounded-lg border border-gray-200 shadow-sm"
                            style={{ maxHeight: '300px' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Function Signature for Coding Questions */}
                  {assessmentType === 'coding' && question.question_data?.function_signature && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Function Signature</label>
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm">
                        <code>
                          def {question.question_data.function_signature.function_name}(
                          {question.question_data.function_signature.parameters?.map((p: any) => `${p.name}: ${p.type}`).join(', ') || ''}
                          ) -&gt; {question.question_data.function_signature.return_type}:
                        </code>
                      </div>
                    </div>
                  )}

                  {/* Constraints for Coding Questions */}
                  {assessmentType === 'coding' && question.question_data?.constraints?.items && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Constraints</label>
                      <div className="bg-blue-50 rounded-lg p-4">
                        <ul className="space-y-1">
                          {question.question_data.constraints.items.map((constraint: string, index: number) => (
                            <li key={index} className="text-sm text-blue-800 font-mono">
                              • {constraint}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Example Test Cases for Coding */}
                  {assessmentType === 'coding' && question.question_data?.example_test_cases && question.question_data.example_test_cases.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Example Test Cases</label>
                      <div className="space-y-3">
                        {question.question_data.example_test_cases.map((testCase: any, tcIndex: number) => (
                          <div key={tcIndex} className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-green-800">Example {tcIndex + 1}</h4>
                            </div>
                            
                            {/* Inputs */}
                            <div className="mb-3">
                              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Input</span>
                              <div className="mt-1 space-y-1">
                                {testCase.inputs?.map((input: any, inputIndex: number) => (
                                  <div key={inputIndex} className="p-2 bg-white rounded border font-mono text-sm">
                                    <span className="text-gray-600">{input.name}:</span> {JSON.stringify(input.value)}
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* Output */}
                            <div className="mb-3">
                              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Expected Output</span>
                              <div className="mt-1 p-2 bg-white rounded border font-mono text-sm">
                                {JSON.stringify(testCase.output?.value)}
                              </div>
                            </div>
                            
                            {/* Explanation */}
                            {testCase.explanation && (
                              <div>
                                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Explanation</span>
                                <div className="mt-1 p-2 bg-white rounded border text-sm text-gray-700">
                                  {testCase.explanation}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hidden Test Cases for Coding */}
                  {assessmentType === 'coding' && question.question_data?.hidden_test_cases && question.question_data.hidden_test_cases.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Hidden Test Cases</label>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-center mb-2">
                          <span className="text-sm font-medium text-purple-800">
                            {question.question_data.hidden_test_cases.length} hidden test case(s)
                          </span>
                          <span className="ml-2 text-xs text-purple-600">
                            (Not visible to candidates)
                          </span>
                        </div>
                        <div className="text-sm text-purple-700">
                          Total weight: {question.question_data.hidden_test_cases.reduce((sum: number, tc: any) => sum + (tc.weight || 1), 0).toFixed(1)} points
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Starter Code for Coding Questions */}
                  {assessmentType === 'coding' && question.starter_code && question.starter_code.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Starter Code Templates</label>
                      <div className="space-y-3">
                        {question.starter_code.map((starter: any, index: number) => (
                          <div key={index} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700 capitalize">
                                {starter.language} Template
                              </span>
                            </div>
                            <pre className="bg-gray-900 text-green-400 p-3 rounded font-mono text-sm overflow-x-auto">
                              <code>{starter.code}</code>
                            </pre>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Complexity Information for Coding Questions */}
                  {(question.question_data?.time_complexity_expected || question.question_data?.space_complexity_expected) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Expected Complexity</label>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {question.question_data.time_complexity_expected && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Time Complexity</span>
                              <div className="mt-1 p-2 bg-white rounded border font-mono text-sm">
                                {question.question_data.time_complexity_expected}
                              </div>
                            </div>
                          )}
                          {question.question_data.space_complexity_expected && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Space Complexity</span>
                              <div className="mt-1 p-2 bg-white rounded border font-mono text-sm">
                                {question.question_data.space_complexity_expected}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Language Restrictions for Coding Questions */}
                  {assessmentType === 'coding' && question.language_restrictions && question.language_restrictions.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Allowed Programming Languages</label>
                      <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex flex-wrap gap-2">
                          {question.language_restrictions.map((lang: string, index: number) => (
                            <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                              {lang.charAt(0).toUpperCase() + lang.slice(1)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Legacy Test Cases for backward compatibility */}
                  {assessmentType === 'coding' && question.question_data?.test_cases && !question.question_data?.example_test_cases && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Test Cases</label>
                      <div className="space-y-3">
                        {question.question_data.test_cases.slice(0, 3).map((testCase: any, tcIndex: number) => (
                          <div key={tcIndex} className="bg-gray-50 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Input</span>
                                <div className="mt-1 p-2 bg-white rounded border font-mono text-sm">
                                  {testCase.input}
                                </div>
                              </div>
                              <div>
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Expected Output</span>
                                <div className="mt-1 p-2 bg-white rounded border font-mono text-sm">
                                  {testCase.expected_output}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {question.question_data.test_cases.length > 3 && (
                          <p className="text-sm text-gray-500 text-center py-2">
                            ... and {question.question_data.test_cases.length - 3} more test cases
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Analysis Questions for Case Studies and Business Simulations */}
                  {(assessmentType === 'case-study' || assessmentType === 'business-simulations') && question.question_data?.analysis_questions && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Analysis Questions</label>
                      <div className="space-y-2">
                        {question.question_data.analysis_questions.map((questionText: string, qIndex: number) => (
                          <div key={qIndex} className="bg-gray-50 rounded-lg p-3">
                            <span className="text-sm font-medium text-gray-600 mr-2">{qIndex + 1}.</span>
                            <span className="text-gray-700">{questionText}</span>
                          </div>
                        ))}
                      </div>
                      {question.question_data.expected_analysis && (
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Expected Analysis Points</label>
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <p className="text-gray-700">{question.question_data.expected_analysis}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Options for MCQ/Fundamentals (including Management MCQ types) */}
                  {(assessmentType === 'aptitude' || 
                    assessmentType === 'fundamentals' || 
                    assessmentType === 'cloud_fundamentals' || 
                    assessmentType === 'presales_fundamentals' || 
                    assessmentType === 'english_communication' ||
                    assessmentType === 'ai_fundamental' ||
                    assessmentType === 'quantitative_ability' ||
                    assessmentType === 'logical_reasoning' ||
                    assessmentType === 'verbal_ability') && question.options && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
                      <div className="space-y-2">
                        {/* Options can be a dict {A: '...'} or an array [{id/value: 'A', text: '...'}] */}
                        {Array.isArray(question.options)
                          ? question.options.map((opt: any, i: number) => {
                              const key = opt.id || opt.value || String.fromCharCode(65 + i)
                              const val = opt.text || opt.label || ''
                              const isCorrect = question.correct_answer?.answer === key
                              return (
                                <div key={key} className={`p-3 rounded-lg border ${
                                  isCorrect ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                                }`}>
                                  <span className="text-sm font-medium text-gray-600 mr-2">{key}.</span>
                                  <span className="text-gray-700">{val}</span>
                                  {isCorrect && (
                                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      Correct Answer
                                    </span>
                                  )}
                                </div>
                              )
                            })
                          : Object.entries(question.options).map(([key, value]: [string, any]) => (
                            <div key={key} className={`p-3 rounded-lg border ${
                              question.correct_answer?.answer === key 
                                ? 'bg-green-50 border-green-200' 
                                : 'bg-gray-50 border-gray-200'
                            }`}>
                              <span className="text-sm font-medium text-gray-600 mr-2">{key}.</span>
                              <span className="text-gray-700">{value}</span>
                              {question.correct_answer?.answer === key && (
                                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Correct Answer
                                </span>
                              )}
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <DocumentTextIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Questions Created Yet</h3>
            <p className="text-gray-500 mb-2">
              No questions found for {getCategoryDisplayName()} - {getAssessmentTypeDisplayName()}
            </p>
            <p className="text-sm text-gray-400 mb-6">
              Questions will automatically appear here once they are added to the question bank.
            </p>
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={() => navigate('/recruiter/question-bank')}
                className="btn btn-primary flex items-center"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Questions to Bank
              </button>
              <button
                onClick={() => loadQuestions()}
                disabled={refreshing || loading}
                className="btn btn-outline flex items-center"
              >
                <ArrowPathIcon className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Check Again'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default QuestionsPage
